/**
 * The Customize Report panel's settings (piece 26, Customize Report; decision
 * 0850; T126). 🔒 A saved customisation is one statement's settings under a
 * name, and nothing more: it defines no field, no formula and no report. The
 * settings are a closed shape, parsed here on the way in and the way out, so a
 * stored row the database cannot check is checked by the one parser both the
 * browser and the server read.
 *
 * 🔨 A statement's options are fixed per statement, never chosen by the person:
 * `CUSTOMISABLE_REPORTS` is the list, and a key it does not name takes no panel.
 */
import { reportSheetIsLandscape } from '../print/reportSheet';
import { addCalendarDays, daysInMonth, isCalendarDay, zeroPad } from '../zonedTime';
import type { LedgerBasis } from '../accounting/postingMap';
import type { ReportColumn } from './reports';

/** The Date Range presets, in the order the panel lists them (Zoho's own). */
export const REPORT_DATE_PRESETS = [
  'today', 'thisWeek', 'thisMonth', 'thisQuarter', 'thisYear', 'lastMonth', 'lastQuarter', 'lastYear', 'yearToDate', 'custom',
] as const;
export type ReportDatePreset = (typeof REPORT_DATE_PRESETS)[number];

export const REPORT_COMPARE_WITH = ['none', 'previousPeriods', 'previousYears'] as const;
export type ReportCompareWith = (typeof REPORT_COMPARE_WITH)[number];

/** The column chooser's items (the owner's Show / Hide Columns shot). */
export const STATEMENT_COLUMN_KEYS = ['account', 'accountCode', 'accountDescription', 'total', 'yearToDate'] as const;
export type StatementColumnKey = (typeof STATEMENT_COLUMN_KEYS)[number];

/** The figure items among them; a statement shows at least one. */
export const FIGURE_COLUMN_KEYS: readonly StatementColumnKey[] = ['total', 'yearToDate'];

export const COMPARE_COUNT_MIN = 1;
export const COMPARE_COUNT_MAX = 12;

/**
 * 🔒 The settings one statement is run with, and the JSON a `SavedReportView`
 * row carries in its `settings` column (piece 26, Data structures).
 */
export interface ReportCustomisation {
  datePreset: ReportDatePreset;
  /** Calendar days, with `custom` alone. */
  from?: string;
  to?: string;
  /** Absent reads the organisation's default basis. */
  basis?: LedgerBasis;
  withoutZeroBalance: boolean;
  compareWith: ReportCompareWith;
  compareCount: number;
  latestFirst: boolean;
  /** The selected columns in their order, `account` first. */
  columns: readonly StatementColumnKey[];
}

/** What one statement's panel offers. */
export interface CustomisationOptions {
  basis: boolean;
  zeroBalance: boolean;
  compare: boolean;
  /** The chooser's items; empty where the statement takes no chooser. */
  columns: readonly StatementColumnKey[];
}

const EVERY_COLUMN: readonly StatementColumnKey[] = STATEMENT_COLUMN_KEYS;
const NO_YEAR_TO_DATE: readonly StatementColumnKey[] = ['account', 'accountCode', 'accountDescription', 'total'];

/**
 * 🔨 The statements taking the panel, and what each takes (piece 26). The
 * Balance Sheet takes no Year To Date because a balance is already reckoned
 * from inception; the Trial Balance takes no comparison because its job is one
 * pair of columns that agree; the two detail reports take a date range alone.
 */
export const CUSTOMISABLE_REPORTS: Readonly<Record<string, CustomisationOptions>> = {
  'profit-and-loss': { basis: true, zeroBalance: true, compare: true, columns: EVERY_COLUMN },
  'balance-sheet': { basis: true, zeroBalance: true, compare: true, columns: NO_YEAR_TO_DATE },
  'cash-flow': { basis: true, zeroBalance: true, compare: true, columns: EVERY_COLUMN },
  'trial-balance': { basis: true, zeroBalance: true, compare: false, columns: NO_YEAR_TO_DATE },
  'general-ledger-detail': { basis: false, zeroBalance: false, compare: false, columns: [] },
  journal: { basis: false, zeroBalance: false, compare: false, columns: [] },
};

export function customisationOptions(key: string): CustomisationOptions | null {
  return CUSTOMISABLE_REPORTS[key] ?? null;
}

/**
 * 🔒 The settings a statement opens on: Year To Date, the default range
 * decision 0111 names; accounts without a zero balance, as Zoho's own default;
 * no comparison; and the Account and Total columns the statement always drew.
 */
export function defaultCustomisation(): ReportCustomisation {
  return { datePreset: 'yearToDate', withoutZeroBalance: true, compareWith: 'none', compareCount: 1, latestFirst: false, columns: ['account', 'total'] };
}

/** Every reason a customisation cannot run on this statement, as sentences; empty where it can. */
export function customisationProblems(key: string, settings: ReportCustomisation): readonly string[] {
  const options = customisationOptions(key);
  if (options === null) return [`${key} takes no Customize Report panel`];
  const problems: string[] = [];
  if (!(REPORT_DATE_PRESETS as readonly string[]).includes(settings.datePreset)) problems.push(`${settings.datePreset} is not a date range preset`);
  if (settings.datePreset === 'custom') {
    if (!isCalendarDay(settings.from) || !isCalendarDay(settings.to)) problems.push('a custom range names its From and its To');
    else if (settings.from! > settings.to!) problems.push('a range starts on or before it ends');
  }
  if (settings.basis !== undefined && settings.basis !== 'Cash' && settings.basis !== 'Accrual') problems.push('the basis is Cash or Accrual');
  if (!(REPORT_COMPARE_WITH as readonly string[]).includes(settings.compareWith)) problems.push(`${settings.compareWith} is not a comparison`);
  if (!Number.isInteger(settings.compareCount) || settings.compareCount < COMPARE_COUNT_MIN || settings.compareCount > COMPARE_COUNT_MAX) {
    problems.push(`the number of periods is ${COMPARE_COUNT_MIN} to ${COMPARE_COUNT_MAX}`);
  }
  if (settings.compareWith !== 'none' && !options.compare) problems.push(`${key} takes no comparison`);
  if (options.columns.length > 0) {
    if (settings.columns[0] !== 'account') problems.push('the Account column is always shown, and first');
    const unknown = settings.columns.filter((column) => !options.columns.includes(column));
    if (unknown.length > 0) problems.push(`${key} offers no ${unknown.join(', ')} column`);
    if (new Set(settings.columns).size !== settings.columns.length) problems.push('a column is chosen once');
    if (!settings.columns.some((column) => FIGURE_COLUMN_KEYS.includes(column))) problems.push('at least one figure column, Total or Year To Date, is shown');
  }
  return problems;
}

/**
 * 🔒 A stored or sent customisation read into the closed shape. A key the
 * shape does not carry is dropped, and an option the statement does not take
 * reads its default, so a view saved before a statement lost an option still
 * opens. A value of the wrong kind reads its default too; `customisationProblems`
 * is what refuses a bad one on the way in.
 */
export function parseCustomisation(key: string, raw: unknown): ReportCustomisation {
  const base = defaultCustomisation();
  const options = customisationOptions(key);
  if (raw === null || typeof raw !== 'object' || options === null) return base;
  const value = raw as Record<string, unknown>;
  const preset = (REPORT_DATE_PRESETS as readonly unknown[]).includes(value.datePreset) ? (value.datePreset as ReportDatePreset) : base.datePreset;
  const compareWith = options.compare && (REPORT_COMPARE_WITH as readonly unknown[]).includes(value.compareWith) ? (value.compareWith as ReportCompareWith) : 'none';
  const count = typeof value.compareCount === 'number' && Number.isInteger(value.compareCount) ? Math.min(COMPARE_COUNT_MAX, Math.max(COMPARE_COUNT_MIN, value.compareCount)) : base.compareCount;
  const columns = Array.isArray(value.columns)
    ? [...new Set(value.columns.filter((column): column is StatementColumnKey => options.columns.includes(column as StatementColumnKey)))]
    : [...base.columns];
  const ordered: StatementColumnKey[] = options.columns.length === 0 ? [] : ['account', ...columns.filter((column) => column !== 'account')];
  const withFigure = ordered.length > 0 && !ordered.some((column) => FIGURE_COLUMN_KEYS.includes(column)) ? [...ordered, 'total' as const] : ordered;
  return {
    datePreset: preset,
    ...(preset === 'custom' && isCalendarDay(value.from) && isCalendarDay(value.to) ? { from: value.from, to: value.to } : {}),
    ...(options.basis && (value.basis === 'Cash' || value.basis === 'Accrual') ? { basis: value.basis } : {}),
    withoutZeroBalance: options.zeroBalance ? value.withoutZeroBalance !== false : true,
    compareWith,
    compareCount: count,
    latestFirst: value.latestFirst === true,
    columns: withFigure,
  };
}

// ---- the filter bar: the range and the basis, one setting with the panel's ----

/** What a statement's filter bar shows: the preset, the two days it stands for, and the basis ('' reads the default). */
export interface StatementBar {
  preset: ReportDatePreset;
  from: string;
  to: string;
  basis: '' | LedgerBasis;
}

/**
 * 🔒 The bar as a customisation reads (T2751; decision 0850): the preset and
 * the days it resolves to against the viewer's day and the fiscal year. A
 * custom range shows its own days, so the pickers and the preset are one
 * setting with the panel's.
 */
export function barOf(settings: ReportCustomisation, today: string, anchor: FiscalAnchor): StatementBar {
  const range = presetRange(settings.datePreset, today, anchor, { ...(settings.from ? { from: settings.from } : {}), ...(settings.to ? { to: settings.to } : {}) });
  return { preset: settings.datePreset, from: range.from, to: range.to, basis: settings.basis ?? '' };
}

/** A new preset on the bar: a named preset fills From and To, and Custom keeps the days showing. */
export function barWithPreset(bar: StatementBar, preset: ReportDatePreset, today: string, anchor: FiscalAnchor): StatementBar {
  if (preset === 'custom') return { ...bar, preset };
  const range = presetRange(preset, today, anchor);
  return { ...bar, preset, from: range.from, to: range.to };
}

/** A day typed into From or To switches the preset to Custom, keeping the other end as it shows. */
export function barWithDay(bar: StatementBar, end: 'from' | 'to', value: string): StatementBar {
  return { ...bar, preset: 'custom', [end]: value };
}

/** The settings with the bar's range and basis written over theirs; the panel's other choices stand. */
export function applyBar(settings: ReportCustomisation, bar: StatementBar): ReportCustomisation {
  const { from: _from, to: _to, basis: _basis, ...rest } = settings;
  void _from;
  void _to;
  void _basis;
  return {
    ...rest,
    datePreset: bar.preset,
    ...(bar.preset === 'custom' ? { from: bar.from, to: bar.to } : {}),
    ...(bar.basis !== '' ? { basis: bar.basis } : {}),
  };
}

// ---- the address: a customisation on the statement's own query ----

/**
 * The query a customised statement is read with, and its two exports. 🔒 The
 * preset travels with the viewer's own calendar day, so the server resolves
 * *This Month* against the day the person is living in and the organisation's
 * own fiscal year.
 */
export function customisationQuery(key: string, settings: ReportCustomisation, today: string): Record<string, string> {
  const options = customisationOptions(key);
  const query: Record<string, string> = { preset: settings.datePreset, today };
  if (settings.datePreset === 'custom' && settings.from && settings.to) {
    query.from = settings.from;
    query.to = settings.to;
  }
  if (options?.basis && settings.basis) query.basis = settings.basis;
  if (options?.zeroBalance && !settings.withoutZeroBalance) query.zeros = 'show';
  if (options?.compare && settings.compareWith !== 'none') {
    query.compare = settings.compareWith;
    query.count = String(settings.compareCount);
    if (settings.latestFirst) query.order = 'latestFirst';
  }
  if (options && options.columns.length > 0) query.columns = settings.columns.join(',');
  return query;
}

/** The query read back into the closed shape, the server's half of `customisationQuery`. */
export function customisationFromQuery(key: string, query: Readonly<Record<string, string | undefined>>): ReportCustomisation {
  const raw: Record<string, unknown> = {
    datePreset: query.preset ?? (query.from || query.to ? 'custom' : undefined),
    from: query.from,
    to: query.to,
    basis: query.basis,
    withoutZeroBalance: query.zeros !== 'show',
    compareWith: query.compare,
    compareCount: query.count && /^\d+$/.test(query.count) ? Number(query.count) : undefined,
    latestFirst: query.order === 'latestFirst',
    ...(query.columns ? { columns: query.columns.split(',').map((column) => column.trim()) } : {}),
  };
  return parseCustomisation(key, raw);
}

// ---- calendar days and the fiscal year ----

/** A range of calendar days, both ends inclusive, each `YYYY-MM-DD`. */
export interface DayRange {
  from: string;
  to: string;
}

/** The month and day the organisation's fiscal year starts on, the month 1 to 12. */
export interface FiscalAnchor {
  month: number;
  day: number;
}

export const CALENDAR_YEAR: FiscalAnchor = { month: 1, day: 1 };

/** The anchor from `Organization.fiscalYearStart`; the calendar year where the organisation holds none (decision 0111). */
export function fiscalAnchorOf(fiscalYearStart: Date | string | null | undefined): FiscalAnchor {
  if (fiscalYearStart === null || fiscalYearStart === undefined) return CALENDAR_YEAR;
  const text = typeof fiscalYearStart === 'string' ? fiscalYearStart.slice(0, 10) : fiscalYearStart.toISOString().slice(0, 10);
  if (!isCalendarDay(text)) return CALENDAR_YEAR;
  return { month: Number(text.slice(5, 7)), day: Number(text.slice(8, 10)) };
}

const dayOf = (year: number, monthIndex: number, day: number): string => {
  const date = new Date(Date.UTC(year, monthIndex, day));
  return `${zeroPad(date.getUTCFullYear(), 4)}-${zeroPad(date.getUTCMonth() + 1)}-${zeroPad(date.getUTCDate())}`;
};
const partsOf = (value: string) => ({ year: Number(value.slice(0, 4)), monthIndex: Number(value.slice(5, 7)) - 1, day: Number(value.slice(8, 10)) });

/** Whole months later or earlier, the day clamped to the month's last, so 31 January plus one month is the last of February. */
export function addMonths(value: string, months: number): string {
  const { year, monthIndex, day } = partsOf(value);
  const target = new Date(Date.UTC(year, monthIndex + months, 1));
  return dayOf(target.getUTCFullYear(), target.getUTCMonth(), Math.min(day, daysInMonth(target.getUTCFullYear(), target.getUTCMonth())));
}

const firstOfMonth = (value: string): string => `${value.slice(0, 7)}-01`;
const endOfMonth = (value: string): string => {
  const { year, monthIndex } = partsOf(value);
  return dayOf(year, monthIndex, daysInMonth(year, monthIndex));
};

/** The fiscal year holding a day. */
export function fiscalYearContaining(value: string, anchor: FiscalAnchor): DayRange {
  const { year } = partsOf(value);
  const startIn = (y: number) => dayOf(y, anchor.month - 1, Math.min(anchor.day, daysInMonth(y, anchor.month - 1)));
  const from = startIn(year) <= value ? startIn(year) : startIn(year - 1);
  return { from, to: addCalendarDays(addMonths(from, 12), -1) };
}

/** The fiscal quarter holding a day: the fiscal year cut into three-month steps from its own start. */
export function fiscalQuarterContaining(value: string, anchor: FiscalAnchor): DayRange {
  const year = fiscalYearContaining(value, anchor);
  for (let step = 0; step < 4; step += 1) {
    const from = addMonths(year.from, step * 3);
    const to = addCalendarDays(addMonths(year.from, (step + 1) * 3), -1);
    if (value >= from && value <= to) return { from, to };
  }
  return { from: year.from, to: year.to };
}

/**
 * 🔒 A preset's range on the viewer's calendar day and the organisation's
 * fiscal year (piece 26). 🔨 A week runs Sunday to Saturday. A month, a
 * quarter and a year are whole, so *This Month* runs to the month's last day.
 */
export function presetRange(preset: ReportDatePreset, today: string, anchor: FiscalAnchor, custom?: Partial<DayRange>): DayRange {
  switch (preset) {
    case 'today':
      return { from: today, to: today };
    case 'thisWeek': {
      const weekday = new Date(`${today}T00:00:00Z`).getUTCDay();
      const from = addCalendarDays(today, -weekday);
      return { from, to: addCalendarDays(from, 6) };
    }
    case 'thisMonth':
      return { from: firstOfMonth(today), to: endOfMonth(today) };
    case 'thisQuarter':
      return fiscalQuarterContaining(today, anchor);
    case 'thisYear':
      return fiscalYearContaining(today, anchor);
    case 'lastMonth': {
      const last = addCalendarDays(firstOfMonth(today), -1);
      return { from: firstOfMonth(last), to: last };
    }
    case 'lastQuarter':
      return fiscalQuarterContaining(addCalendarDays(fiscalQuarterContaining(today, anchor).from, -1), anchor);
    case 'lastYear':
      return fiscalYearContaining(addCalendarDays(fiscalYearContaining(today, anchor).from, -1), anchor);
    case 'yearToDate':
      return { from: fiscalYearContaining(today, anchor).from, to: today };
    case 'custom':
      return isCalendarDay(custom?.from) && isCalendarDay(custom?.to) && custom!.from! <= custom!.to! ? { from: custom!.from!, to: custom!.to! } : { from: fiscalYearContaining(today, anchor).from, to: today };
  }
}

/** Year To Date for a range: the fiscal year's start to the range's end (piece 26). */
export function yearToDateOf(range: DayRange, anchor: FiscalAnchor): DayRange {
  return { from: fiscalYearContaining(range.to, anchor).from, to: range.to };
}

/** True where a range starts on a month's first day and ends on a month's last. */
export function isWholeMonths(range: DayRange): boolean {
  return range.from.endsWith('-01') && range.to === endOfMonth(range.to);
}

function monthsSpanned(range: DayRange): number {
  const from = partsOf(range.from);
  const to = partsOf(range.to);
  return (to.year - from.year) * 12 + (to.monthIndex - from.monthIndex) + 1;
}

function daysSpanned(range: DayRange): number {
  return Math.round((new Date(`${range.to}T00:00:00Z`).getTime() - new Date(`${range.from}T00:00:00Z`).getTime()) / 86_400_000) + 1;
}

/**
 * 🔒 The comparison periods, nearest first (piece 26). A previous period is
 * the range's own length stepped back, in whole months where the range is
 * whole months and in days where it is not. A previous year is the same range
 * a year earlier, a whole month kept whole.
 */
export function comparisonPeriods(range: DayRange, compareWith: ReportCompareWith, count: number): DayRange[] {
  if (compareWith === 'none') return [];
  const periods: DayRange[] = [];
  const whole = isWholeMonths(range);
  for (let step = 1; step <= count; step += 1) {
    if (compareWith === 'previousYears') {
      const from = addMonths(range.from, -12 * step);
      periods.push({ from, to: whole ? endOfMonth(addMonths(range.to, -12 * step)) : addMonths(range.to, -12 * step) });
    } else if (whole) {
      const months = monthsSpanned(range);
      const from = addMonths(range.from, -months * step);
      periods.push({ from, to: endOfMonth(addMonths(from, months - 1)) });
    } else {
      const days = daysSpanned(range);
      periods.push({ from: addCalendarDays(range.from, -days * step), to: addCalendarDays(range.to, -days * step) });
    }
  }
  return periods;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const monthCaption = (value: string) => `${MONTHS[partsOf(value).monthIndex]} ${value.slice(0, 4)}`;

/**
 * 🔒 A period's caption as its column heads it, the owner's Zoho statement's
 * shape: *Jan 2026* for one month, *Jan 2026 - Mar 2026* for whole months, and
 * the two days otherwise.
 */
export function periodCaption(range: DayRange): string {
  if (isWholeMonths(range)) {
    return range.from.slice(0, 7) === range.to.slice(0, 7) ? monthCaption(range.from) : `${monthCaption(range.from)} - ${monthCaption(range.to)}`;
  }
  return range.from === range.to ? range.from : `${range.from} - ${range.to}`;
}

// ---- the added columns ----

/**
 * 🔒 One figure column copied once per period (decision 0545): each copy keeps
 * the base column's kind and its cost-or-margin declaration, so the report
 * set's withholding strips every copy, its cells and its totals, and a period
 * added later cannot carry a figure the column was declared to hide.
 */
export function periodColumns(base: ReportColumn, periods: readonly { key: string; label: string }[]): ReportColumn[] {
  return periods.map((period) => ({ ...base, key: period.key, label: period.label }));
}

/** A figure column counts toward the sheet's width where it is money, a number or a percent. */
export function figureColumnCount(columns: readonly ReportColumn[]): number {
  return columns.filter((column) => column.kind === 'money' || column.kind === 'number' || column.kind === 'percent').length;
}

/**
 * 🔒 A sheet carrying more than seven columns prints landscape (piece 26; decision
 * 1003, amending 0850's four figure columns). The denser report face fits seven
 * upright, so a compared statement of five periods stays on its side of the page.
 */
export function sheetIsLandscape(columns: readonly ReportColumn[]): boolean {
  return reportSheetIsLandscape(columns.length);
}

/** The saved-view name's longest length, so a Select row never runs off its box. */
export const SAVED_REPORT_VIEW_NAME_MAX = 80;

/** Every reason a saved view's name cannot stand; empty where it can. */
export function savedViewNameProblem(name: string): string | null {
  const trimmed = name.trim();
  if (trimmed === '') return 'a saved report view carries a name';
  if (trimmed.length > SAVED_REPORT_VIEW_NAME_MAX) return `a saved report view's name is ${SAVED_REPORT_VIEW_NAME_MAX} characters or fewer`;
  return null;
}

/** The answer shape one saved view travels in: the row, the settings parsed, and whether the viewer owns it. */
export interface SavedReportViewAnswer {
  id: string;
  version: number;
  reportKey: string;
  name: string;
  isShared: boolean;
  ownedByViewer: boolean;
  ownerName: string;
  settings: ReportCustomisation;
  updatedAt: string;
}
